import { Grid, Tooltip, Typography } from '@mui/material';
import { IComponentCss, LinearProgressChart, numberHelper, Widget, XGridTableWrap } from '@tap/mui-ui';
import {
    useGridApiRef,
} from '@mui/x-data-grid-pro';
import { DataCollection, DocUpdateIssueSlaReportRow } from '@tap/data-providers';
import { useDocumentSchemaContext } from '@tap/doc-ui';
import { HomeFilter, HomeFilterValue } from './HomeFilter';
import { dateTimeHelper } from '@tap/utils';

/*
*   Виджет: Время решения, последние 60 дней
*/

const css: IComponentCss = {
    widget: {
        height: '560px',
        padding: '20px 0px 0px',
    },
    dateTimeUpdate: {
        position: 'absolute',
        color: '#999999',
        top: '22px',
        right: '22px',
        fontSize: '14px',
    },
    widgetTitle: {
        paddingRight: '30px',
        borderBottom: '1px solid #E0E0E0',
        padding: '0px 20px 20px',
        marginBottom: '0px',
    },
    tableWrap: {
        '& .head-group-row': {
            '&:hover': { backgroundColor: 'inherit  !important' },
            '& > *': {
                ':focus-within': {
                    outline: 'none !important',
                },
            },
            '& > div:not(:first-of-type) > *': {
                display: 'none !important',
                ':focus-within': {
                    outline: 'none !important',
                },
            },
        },
        '& .head-group-cell': {
            overflow: 'visible !important',
        },
        '& .item-group-cell': {
            paddingLeft: '38px !important',
            ' .MuiDataGrid-treeDataGroupingCellToggle': {
                display: 'none !important',
            },
            '& *': {
                textOverflow: 'ellipsis',
                overflow: 'hidden',
            },
        },
        '.MuiDataGrid-root': {
            border: 'none',
            borderRadius: '0px',
        },
    },
    chartStyle: {
        width: '70%',
        height: '30px',
        borderRadius: 0,
        marginRight: '10px',
        marginLeft: '10px',
    },
    tableTextStyle: {
        width: '10%',
    },
    decisionTimeGrid: {
        width: '100%',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
};

export type DecisionTimeWidgetProps = {
    reportData?: DataCollection<DocUpdateIssueSlaReportRow[]>,
    reportLoading: boolean,
}

const calcTableData = (reportData: DataCollection<DocUpdateIssueSlaReportRow[]> | undefined, filterValue: HomeFilterValue) => {
    if (!reportData?.data?.length) return [];



    const statRows = reportData?.data
        ?.filter(item => {
            return (filterValue?.departmentIds?.length === 0 || filterValue?.departmentIds?.includes(item.department?.id))
                && (filterValue?.isueType?.length === 0 || filterValue?.isueType?.includes(`${item.issueType}-${item.documentCode}`))
                && (filterValue?.manufactories?.length === 0 || filterValue?.manufactories?.includes(item.department?.manufactory))
        });

    const groups = {} as any;
    statRows.forEach(item => {
        const groupId = `${item.issueType}-${item.documentCode}`;
        groups[groupId] = groups[groupId] || {
            id: groupId,
            documentCode: item.documentCode,
            issueType: item.issueType,
            totalCount: 0,
            completedOnTimeCount: 0,
            totalTime: 0,
        };

        groups[groupId].totalCount = groups[groupId].totalCount + item.totalCount;
        groups[groupId].completedOnTimeCount = groups[groupId].completedOnTimeCount + item.completedOnTimeCount;
        groups[groupId].totalTime = groups[groupId].totalTime + (item.totalCount * item.avgTotalTime);
    });

    return Object.keys(groups).map(k => groups[k]).map(item => ({
        ...item,
        avgTotalTime: item.totalTime / item.totalCount,
    }));
}

export default function DecisionTimeWidget({ reportData, reportLoading }: DecisionTimeWidgetProps) {
    const { schemaList } = useDocumentSchemaContext();
    const { filterValue } = HomeFilter.useContext();

    const isueTypeList = schemaList
        .filter(dc => dc.settings.typeList)
        .map(dc => dc.settings.typeList.map((item: any) => {
            return {
                ...item,
                code: `${item.code}-${dc.documentCode}`,
            }
        }),
        )
        .flat();

    const rows = calcTableData(reportData, filterValue);
    const xGridApiRef = useGridApiRef();

    return (
        <Widget
            title={'Статистика - Время решения, последние 60 дней'}
            widgetSx={css.widget}
            titleSx={css.widgetTitle}
        >


            {Boolean(reportData?.changedAtUtc) && (
                <Grid sx={css.dateTimeUpdate}>
                    <Tooltip
                        title={`Дата обновления: ${dateTimeHelper.format(reportData?.changedAtUtc, 'dd.MM.yyyy  HH:mm')}`}
                    >
                        <span>
                            обновлено {dateTimeHelper.format(reportData?.changedAtUtc, 'HH:mm')}
                        </span>
                    </Tooltip>
                </Grid>
            )}
            <Grid container sx={css.tableWrap}>
                <XGridTableWrap
                    rows={rows as any}
                    dataIsLoading={reportLoading}
                    tableProps={{
                        apiRef: xGridApiRef,
                        disableColumnFilter: true,
                        slots: {
                            toolbar: null,
                        },
                    }}
                    columns={[
                        {
                            headerName: 'Тип запроса',
                            field: 'issueType',
                            width: 350,
                            sortable: false,
                            type: 'string',
                            renderCell: (cellParams) => isueTypeList?.find((el: any) => el.code === `${cellParams.row.issueType}-${cellParams.row.documentCode}`)?.name || '-',
                        },
                        {
                            headerName: 'Время решения % вовремя',
                            field: 'completedOnTimeCount',
                            width: 350,
                            sortable: false,
                            renderCell: (cellParams) => {
                                const responseTime = numberHelper.formatNumberWithDotDelimiter((cellParams.row.completedOnTimeCount / cellParams.row.totalCount) * 100, 2);
                                return (
                                    <Grid display={'flex'} sx={css.decisionTimeGrid}>
                                        <Typography sx={css.tableTextStyle}>{`${responseTime}%`}</Typography>
                                        <LinearProgressChart
                                            value={responseTime > 100 ? 100 : responseTime}
                                            chartStyle={css.chartStyle}
                                            colorBar='#4caf50'
                                        />
                                        <Typography sx={css.tableTextStyle}>{`${cellParams.row.completedOnTimeCount}/${cellParams.row.totalCount}`}</Typography>
                                    </Grid>
                                )
                            },
                        },
                        {
                            headerName: 'Время решения % Avg',
                            field: 'avgTotalTime',
                            width: 182,
                            sortable: false,
                            type: 'number',
                            renderCell: cellParams => dateTimeHelper.timeFormat(cellParams.row.avgTotalTime * 1000),
                        },
                    ]}
                />
            </Grid>
        </Widget >
    );
}

